/**
 * useExchangeRates Hook
 * 
 * Hook for managing exchange rates and currency conversions
 */

import { useState, useEffect, useCallback } from 'react';
import { exchangeRateService } from '../lib/services/exchangeRateService';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { Currency } from '../types/currency';

interface ExchangeRate {
  baseCurrency: Currency;
  targetCurrency: Currency;
  rate: number;
  updatedAt: string;
}

interface UseExchangeRatesReturn {
  /**
   * Get exchange rate between two currencies
   */
  getRate: (from: Currency, to: Currency) => Promise<number | null>;
  
  /**
   * Convert amount from one currency to another
   */
  convert: (amount: number, from: Currency, to: Currency) => Promise<number | null>;
  
  /**
   * All available exchange rates
   */
  rates: ExchangeRate[];
  
  /**
   * Whether rates are being loaded
   */
  isLoading: boolean;
  
  /**
   * Whether user has configured API key
   */
  hasApiKey: boolean;
  
  /**
   * Manually refresh all rates
   */
  refreshRates: () => Promise<void>;
  
  /**
   * Last time rates were updated
   */
  lastUpdate: Date | null;
}

export function useExchangeRates(): UseExchangeRatesReturn {
  const { user } = useAuth();
  const [rates, setRates] = useState<ExchangeRate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Check if user has API key configured
  useEffect(() => {
    checkApiKey();
  }, [user?.id]);

  // Load rates when user logs in
  useEffect(() => {
    if (user?.id && hasApiKey) {
      loadRates();
    }
  }, [user?.id, hasApiKey]);

  const checkApiKey = async () => {
    if (!user) return;
    
    try {
      const { data } = await supabase.auth.getUser();
      const apiKey = data.user?.user_metadata?.exchangerate_api_key;
      setHasApiKey(!!apiKey);
    } catch (error) {
      console.error('[useExchangeRates] Error checking API key:', error);
    }
  };

  const loadRates = async () => {
    if (!user?.id) return;
    
    try {
      setIsLoading(true);
      const allRates = await exchangeRateService.getAllRates(user.id);
      
      setRates(allRates.map(r => ({
        baseCurrency: r.base_currency,
        targetCurrency: r.target_currency,
        rate: r.rate,
        updatedAt: r.updated_at,
      })));

      // Get last update date
      if (allRates.length > 0) {
        const mostRecent = allRates.reduce((latest, current) => {
          return new Date(current.updated_at) > new Date(latest.updated_at) ? current : latest;
        });
        setLastUpdate(new Date(mostRecent.updated_at));
      }
    } catch (error) {
      console.error('[useExchangeRates] Error loading rates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRate = useCallback(async (from: Currency, to: Currency): Promise<number | null> => {
    if (!user?.id) {
      console.warn('[useExchangeRates] User not authenticated');
      return null;
    }

    if (!hasApiKey) {
      console.warn('[useExchangeRates] No API key configured');
      return null;
    }

    // Check if we need to update rates (auto-refresh if > 24h)
    const needsUpdate = await exchangeRateService.needsUpdate(user.id);
    
    if (needsUpdate) {
      console.log('[useExchangeRates] Rates outdated, auto-refreshing...');
      await refreshRates();
    }

    return await exchangeRateService.getRate(user.id, from, to);
  }, [user?.id, hasApiKey]);

  const convert = useCallback(async (
    amount: number,
    from: Currency,
    to: Currency
  ): Promise<number | null> => {
    if (!user?.id) return null;
    if (!hasApiKey) return null;

    return await exchangeRateService.convert(user.id, amount, from, to);
  }, [user?.id, hasApiKey]);

  const refreshRates = useCallback(async (): Promise<void> => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    if (!hasApiKey) {
      throw new Error('No API key configured');
    }

    try {
      setIsLoading(true);

      // Get API key
      const { data } = await supabase.auth.getUser();
      const apiKey = data.user?.user_metadata?.exchangerate_api_key;

      if (!apiKey) {
        throw new Error('API key not found');
      }

      // Update all rates
      await exchangeRateService.updateAllRates(user.id, apiKey);
      
      // Reload rates
      await loadRates();
    } catch (error) {
      console.error('[useExchangeRates] Error refreshing rates:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, hasApiKey]);

  return {
    getRate,
    convert,
    rates,
    isLoading,
    hasApiKey,
    refreshRates,
    lastUpdate,
  };
}
